import { useState } from "react";

import { formatCountdown } from "@/hooks/useTestCountdown";
import type { Attempt } from "@/api/tests-api";

interface TestStartGateProps {
  attempt: Attempt;
  busy: boolean;
  onStartDryRun: () => void;
  onSkipDryRun: () => void;
  onBeginAssessment: () => void;
}

/**
 * What a candidate sees before the clock starts.
 *
 * The whole point of this screen is that **nothing is running yet**.
 * Reading the brief, deciding about the practice run, and getting one's
 * bearings all happen with the assessment clock stopped. It starts on a
 * deliberate press of "Start the test" and not a moment earlier — an
 * earlier version began the countdown the instant the candidate opened
 * the exam, so they lost time to reading the instructions.
 *
 * The confirmation before starting is a real one, because the decision
 * is irreversible: the clock does not stop again.
 */
export function TestStartGate({
  attempt,
  busy,
  onStartDryRun,
  onSkipDryRun,
  onBeginAssessment,
}: TestStartGateProps) {
  const [confirming, setConfirming] = useState(false);

  const practiceAvailable =
    attempt.has_dry_run && !attempt.dry_run_finished && !attempt.started;

  return (
    <div
      data-cy="test-start-gate"
      className="mx-auto w-full max-w-2xl px-6 py-10"
    >
      <h1 className="text-xl font-semibold text-foreground">{attempt.title}</h1>

      <div className="mt-4 rounded-xl border border-border bg-background-card p-5">
        <p className="text-sm text-foreground">
          This assessment lasts{" "}
          <strong className="font-mono">
            {formatCountdown(attempt.duration_seconds || attempt.seconds_remaining || 0)}
          </strong>{" "}
          once you start it, across {attempt.cases.length} case
          {attempt.cases.length === 1 ? "" : "s"}.
        </p>
        <ul className="mt-3 flex list-disc flex-col gap-1 pl-5 text-sm text-foreground-muted">
          <li>The clock has not started yet. It starts when you press Start.</li>
          <li>Once started it does not pause, so begin when you are ready.</li>
          <li>You can work through the cases in any order.</li>
          <li>
            Leaving the assessment ends it — you cannot come back to it
            later.
          </li>
        </ul>
      </div>

      {practiceAvailable && (
        <div
          data-cy="dry-run-offer"
          className="mt-4 rounded-xl border border-border bg-background-card p-5"
        >
          <h2 className="text-sm font-semibold text-foreground">
            Practice run{" "}
            <span className="font-normal text-foreground-muted">
              (optional)
            </span>
          </h2>
          <p className="mt-1 text-sm text-foreground-muted">
            A short run-through on separate cases, so you can get used to
            viewing the images and recording your report.{" "}
            <strong className="text-foreground">
              None of this time counts towards your assessment.
            </strong>
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              data-cy="btn-start-dry-run"
              disabled={busy}
              onClick={onStartDryRun}
              className="sk-btn sk-btn-primary"
            >
              Try the practice run
            </button>
            <button
              type="button"
              data-cy="btn-skip-dry-run"
              disabled={busy}
              onClick={onSkipDryRun}
              className="rounded-md border border-border px-4 py-2 text-sm text-foreground hover:border-accent disabled:opacity-50"
            >
              Skip it
            </button>
          </div>
        </div>
      )}

      <div className="mt-6">
        {confirming ? (
          <div
            data-cy="start-confirm"
            role="alertdialog"
            className="rounded-xl border-2 border-accent bg-background-card p-5"
          >
            <p className="text-sm font-medium text-foreground">
              Start the assessment now?
            </p>
            <p className="mt-1 text-sm text-foreground-muted">
              The clock starts immediately and will not pause.
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                data-cy="btn-confirm-start"
                disabled={busy}
                onClick={onBeginAssessment}
                className="sk-btn sk-btn-primary"
              >
                {busy ? "Starting…" : "Yes, start now"}
              </button>
              <button
                type="button"
                data-cy="btn-cancel-start"
                onClick={() => setConfirming(false)}
                className="rounded-md border border-border px-4 py-2 text-sm text-foreground"
              >
                Not yet
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            data-cy="btn-start-the-test"
            disabled={busy}
            onClick={() => setConfirming(true)}
            className="sk-btn sk-btn-primary w-full"
          >
            Start the test
          </button>
        )}
      </div>
    </div>
  );
}
